(() => {
  const countWords = (text = "") => {
    const matches = text.match(/\S+/g);
    return matches ? matches.length : 0;
  };

  const hasMeaningfulDescriptionContent = (container) => {
    const meaningfulMediaSelector = "img,video,iframe,svg,canvas,object,embed";

    if (container.querySelector(meaningfulMediaSelector)) {
      return true;
    }

    const normalizedText = (container.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/\u200B/g, "")
      .trim();

    return normalizedText.length > 0;
  };

  const splitDescriptionNode = (sourceNode, visibleTarget, hiddenTarget, state) => {
    if (sourceNode.nodeType === Node.TEXT_NODE) {
      const text = sourceNode.textContent ?? "";
      const tokens = text.match(/\S+\s*/g);

      if (!tokens) {
        const target = state.splitStarted ? hiddenTarget : visibleTarget;
        target.appendChild(document.createTextNode(text));
        return;
      }

      if (state.splitStarted || state.remainingWords <= 0) {
        hiddenTarget.appendChild(document.createTextNode(text));
        state.splitStarted = true;
        return;
      }

      if (tokens.length <= state.remainingWords) {
        visibleTarget.appendChild(document.createTextNode(text));
        state.remainingWords -= tokens.length;
        return;
      }

      const wordsToKeep = Math.max(state.remainingWords, 0);
      const visibleText = tokens.slice(0, wordsToKeep).join("");
      const hiddenText = tokens.slice(wordsToKeep).join("");

      if (visibleText) {
        visibleTarget.appendChild(document.createTextNode(visibleText));
      }

      if (hiddenText) {
        hiddenTarget.appendChild(document.createTextNode(hiddenText));
      }

      state.remainingWords = 0;
      state.splitStarted = true;
      return;
    }

    if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const sourceElement = sourceNode;

    if (["SCRIPT", "STYLE"].includes(sourceElement.tagName)) {
      return;
    }

    const preserveAsWholeBlock = ["P", "H1", "H2", "H3", "H4", "H5", "H6"].includes(sourceElement.tagName);

    if (preserveAsWholeBlock) {
      const blockWords = countWords(sourceElement.textContent || "");

      if (state.splitStarted || state.remainingWords <= 0) {
        hiddenTarget.appendChild(sourceElement.cloneNode(true));
        state.splitStarted = true;
        return;
      }

      visibleTarget.appendChild(sourceElement.cloneNode(true));

      if (blockWords <= state.remainingWords) {
        state.remainingWords -= blockWords;
      } else {
        state.remainingWords = 0;
        state.splitStarted = true;
      }

      return;
    }

    const visibleClone = sourceElement.cloneNode(false);
    const hiddenClone = sourceElement.cloneNode(false);

    for (const childNode of Array.from(sourceElement.childNodes)) {
      splitDescriptionNode(childNode, visibleClone, hiddenClone, state);
    }

    if (visibleClone.childNodes.length > 0 || ["BR", "HR", "IMG"].includes(visibleClone.nodeName)) {
      visibleTarget.appendChild(visibleClone);
    }

    if (hiddenClone.childNodes.length > 0 || ["BR", "HR", "IMG"].includes(hiddenClone.nodeName)) {
      hiddenTarget.appendChild(hiddenClone);
    }
  };

  const getMaxWords = (container) => {
    const isMobile = window.innerWidth < 768;
    const maxWordsDesktop = parseInt(container.getAttribute("data-description-max-words-desktop") ?? "120", 10) || 120;
    const maxWordsMobile = parseInt(container.getAttribute("data-description-max-words-mobile") ?? "80", 10) || 80;
    return Math.max(1, isMobile ? maxWordsMobile : maxWordsDesktop);
  };

  const initCollapsibleDescriptions = (root = document) => {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;

    scope.querySelectorAll("[data-description-collapsible]:not([data-description-collapsible-ready])").forEach((container) => {
      if (!hasMeaningfulDescriptionContent(container)) {
        container.remove();
        return;
      }

      const maxWords = getMaxWords(container);
      const moreLabel = container.getAttribute("data-description-more-label") || "Leer más";
      const lessLabel = container.getAttribute("data-description-less-label") || "Leer menos";
      const originalHtml = container.innerHTML;

      if (countWords(container.textContent || "") <= maxWords) {
        container.setAttribute("data-description-collapsible-ready", "true");
        return;
      }

      const visibleFragment = document.createDocumentFragment();
      const hiddenFragment = document.createDocumentFragment();
      const state = { remainingWords: maxWords, splitStarted: false };

      Array.from(container.childNodes).forEach((childNode) => {
        splitDescriptionNode(childNode, visibleFragment, hiddenFragment, state);
      });

      const tempVisibleWrapper = document.createElement("div");
      tempVisibleWrapper.appendChild(visibleFragment);

      const tempHiddenWrapper = document.createElement("div");
      tempHiddenWrapper.appendChild(hiddenFragment);

      const truncatedHtml = tempVisibleWrapper.innerHTML.trim();
      const remainingContent = tempHiddenWrapper.innerHTML.trim();
      const originalHtmlTrimmed = originalHtml.trim();

      if (!truncatedHtml || !remainingContent || truncatedHtml === originalHtmlTrimmed) {
        container.setAttribute("data-description-collapsible-ready", "true");
        return;
      }

      // Create wrapper for truncated content (always visible)
      const visibleWrapper = document.createElement("div");
      visibleWrapper.className = "description-visible";
      visibleWrapper.innerHTML = truncatedHtml;

      // Create wrapper for remaining content (hidden with opacity 0, but in DOM for SEO)
      const hiddenWrapper = document.createElement("div");
      hiddenWrapper.className = "description-hidden";
      hiddenWrapper.style.opacity = "0";
      hiddenWrapper.style.maxHeight = "0";
      hiddenWrapper.style.overflow = "hidden";
      hiddenWrapper.style.transition = "opacity 0.3s ease, max-height 0.3s ease";
      hiddenWrapper.innerHTML = remainingContent;

      // Clear container and add both wrappers
      container.innerHTML = "";
      container.appendChild(visibleWrapper);
      container.appendChild(hiddenWrapper);

      const toggleButton = document.createElement("button");
      toggleButton.type = "button";
      toggleButton.className = "description-collapsible-toggle";
      toggleButton.setAttribute("aria-expanded", "false");
      toggleButton.textContent = moreLabel;

      toggleButton.addEventListener("click", () => {
        const isExpanded = toggleButton.getAttribute("aria-expanded") === "true";
        
        if (isExpanded) {
          hiddenWrapper.style.opacity = "0";
          hiddenWrapper.style.maxHeight = "0";
          toggleButton.textContent = moreLabel;
          toggleButton.setAttribute("aria-expanded", "false");
        } else {
          hiddenWrapper.style.opacity = "1";
          hiddenWrapper.style.maxHeight = "none";
          toggleButton.textContent = lessLabel;
          toggleButton.setAttribute("aria-expanded", "true");
        }
      });

      container.insertAdjacentElement("afterend", toggleButton);
      container.setAttribute("data-description-collapsible-ready", "true");
    });
  };

  const fdGetOptionValuesForForm = (form) =>
    Array.from(form.elements)
      .filter((item) => item.matches?.("input[data-option-position]:checked"))
      .sort((a, b) => parseInt(a.getAttribute("data-option-position"), 10) - parseInt(b.getAttribute("data-option-position"), 10))
      .map((input) => input.value);

  const fdGetVariantPickerForForm = (formId) =>
    document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);

  const fdLoadingLabel = window.themeVariables?.strings?.loading || "Loading...";
  const fdChooseOptionsLabel = window.themeVariables?.strings?.chooseOptions || "Choose options";

  try {
    initCollapsibleDescriptions(document);
  } catch (error) {
    console.error("[fd-theme] initCollapsibleDescriptions failed", error);
  }

  document.addEventListener("shopify:section:load", (event) => {
    try {
      initCollapsibleDescriptions(event.target || document);
    } catch (error) {
      console.error("[fd-theme] section initCollapsibleDescriptions failed", error);
    }
  });

  // ── Video slide placeholder ────────────────────────────────────────────────
  //
  // Background video slides paint black until the video renders its first frame.
  // snippets/fd-video-placeholder.liquid draws an image on top of the
  // <video-media> to cover that gap; this controller cross-fades it out the
  // moment the video actually has a frame on screen.
  //
  // The gating signal is requestVideoFrameCallback, which fires exactly when a
  // frame has been presented to the compositor. We do NOT key off the data
  // events (loadeddata/canplay/playing) or the <video-media> [loaded]/[playing]
  // attributes, because those only mean the media is buffered/decoded — with HLS
  // (Shopify serves theme videos as .m3u8) they can fire a beat before the first
  // frame is painted, so the placeholder would fade over a still-black <video>.
  // Keying off the presented frame keeps the swap seamless (no black flash).
  const FD_PLACEHOLDER_READY_EVENTS = ["loadeddata", "canplay", "playing"];
  // Safety net: if no frame is ever presented (autoplay blocked and the video
  // never renders), reveal anyway so the placeholder can't get stuck on screen.
  const FD_PLACEHOLDER_MAX_WAIT = 4000;

  const fdRevealVideoPlaceholder = (placeholder) => {
    placeholder.classList.add("fd-video-placeholder--loaded");
  };

  const fdInitVideoPlaceholder = (placeholder) => {
    if (placeholder.dataset.fdPlaceholderReady) {
      return;
    }
    placeholder.dataset.fdPlaceholderReady = "true";

    const media = placeholder.closest(".content-box__background-media");
    const video = media?.querySelector("video");

    // External-host videos (YouTube/Vimeo) render an <iframe>, not a <video>, and
    // expose no native first-frame event — fall back to the media:play event that
    // <video-media> bubbles when it starts playing.
    if (!video) {
      const videoMedia = media?.querySelector("video-media");
      if (videoMedia?.hasAttribute("playing")) {
        fdRevealVideoPlaceholder(placeholder);
        return;
      }
      media?.addEventListener("media:play", () => fdRevealVideoPlaceholder(placeholder), { once: true });
      return;
    }

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      fdRevealVideoPlaceholder(placeholder);
    };

    // Preferred path: reveal on the first frame painted to the compositor.
    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(reveal);
      setTimeout(reveal, FD_PLACEHOLDER_MAX_WAIT);
      return;
    }

    // Fallback for browsers without requestVideoFrameCallback (e.g. Firefox):
    // approximate with the data events / current readyState.
    if (video.readyState >= 2) {
      reveal();
      return;
    }
    const onReady = () => {
      reveal();
      FD_PLACEHOLDER_READY_EVENTS.forEach((type) => video.removeEventListener(type, onReady));
    };
    FD_PLACEHOLDER_READY_EVENTS.forEach((type) => video.addEventListener(type, onReady));
    setTimeout(reveal, FD_PLACEHOLDER_MAX_WAIT);
  };

  const initVideoPlaceholders = (root = document) => {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    scope.querySelectorAll(".fd-video-placeholder").forEach(fdInitVideoPlaceholder);
  };

  try {
    initVideoPlaceholders(document);
  } catch (error) {
    console.error("[fd-theme] initVideoPlaceholders failed", error);
  }

  document.addEventListener("shopify:section:load", (event) => {
    try {
      initVideoPlaceholders(event.target || document);
    } catch (error) {
      console.error("[fd-theme] section initVideoPlaceholders failed", error);
    }
  });

  // ── Variant-selection guard ────────────────────────────────────────────────
  //
  // VariantPicker.selectCombination() is async: it fetches new section HTML,
  // then updates form[name="id"].value and dispatches variant:change. Until
  // that completes, the form's id input still holds the previous variant —
  // submitting during this window adds the wrong item.
  //
  // The race is more subtle with rapid option changes: the first fetch may
  // complete and re-render buy-buttons (giving a fresh, enabled button) while a
  // second fetch is still in-flight. We solve this with three layers:
  //
  //  1. A per-form pending counter — incremented on each option change,
  //     decremented on each variant:change. Buttons are kept disabled until
  //     the counter reaches zero (all concurrent fetches resolved).
  //
  //  2. A "requires full explicit selection" tracker — when the server renders
  //     the variant picker with `requires-selection` (no URL variant + >1
  //     variant), the ATC must stay disabled with "Choose options" until the
  //     user has clicked at least one input per option position. Without this,
  //     a partial selection (e.g. size only) would let the server resolve a
  //     "first available" variant and re-render the ATC as enabled.
  //
  //  3. A MutationObserver that re-applies the disabled state to any fresh
  //     submit button injected by product:rerender while either condition
  //     above is still active.
  //
  // ──────────────────────────────────────────────────────────────────────────

  const fdPending = new Map(); // Map<formId, number>
  const fdFallbackTimers = new Map(); // Map<formId, timeoutId>
  const fdLastResolveAt = new Map(); // Map<formId, timestamp>
  const fdNeedsSync = new Map(); // Map<formId, boolean>
  const fdRequiresSelection = new Set(); // Set<formId>
  const fdClickedPositions = new Map(); // Map<formId, Set<string>>

  const fdGetFormId = (form) => {
    if (!form || typeof form.getAttribute !== "function") return "";
    return (form.getAttribute("id") || "").trim();
  };

  const fdResolveFormId = (value, fallbackTarget) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (value && typeof value.getAttribute === "function") {
      if (value.matches?.("form")) {
        return fdGetFormId(value);
      }

      if (value.matches?.("input,select,textarea,button")) {
        return fdGetFormId(value.form || value.closest("form"));
      }
    }

    return fdGetFormId(fallbackTarget);
  };

  const fdGetFormForInput = (input) => {
    const fid = input.getAttribute("form");
    if (fid && document.forms[fid]) {
      return document.forms[fid];
    }
    return input.closest("form");
  };

  const fdGetFormIdForSubmitButton = (btn) => {
    const fid = btn.getAttribute("form");
    if (fid) return fid;
    return fdGetFormId(btn.form || btn.closest("form"));
  };

  const fdHasFullSelection = (formId) => {
    if (!fdRequiresSelection.has(formId)) return true;
    const picker = fdGetVariantPickerForForm(formId);
    if (!picker) return true;
    const total = parseInt(picker.getAttribute("total-options") || "0", 10);
    if (total <= 0) return true;
    const clicked = fdClickedPositions.get(formId);
    return !!clicked && clicked.size >= total;
  };

  const fdApplyButtonOverride = (btn, label, state) => {
    if (btn.dataset.fdButtonState === state) return;
    const buttonContent = btn.querySelector("button-content");
    if (buttonContent) {
      if (btn.dataset.fdOriginalButtonText === undefined) {
        btn.dataset.fdOriginalButtonText = buttonContent.textContent || "";
      }
      buttonContent.textContent = label;
    }
    btn.dataset.fdButtonState = state;
    btn.disabled = true;
  };

  const fdClearButtonOverride = (btn) => {
    if (!btn.dataset.fdButtonState) return;
    const buttonContent = btn.querySelector("button-content");
    if (buttonContent && btn.dataset.fdOriginalButtonText !== undefined) {
      buttonContent.textContent = btn.dataset.fdOriginalButtonText;
    }
    delete btn.dataset.fdOriginalButtonText;
    delete btn.dataset.fdButtonState;
    delete btn.dataset.fdScrollToPicker;
    // Note: server may have rendered btn with disabled=true (sold out / unavailable).
    // We only clear our override; the surrounding rerender will replace the button
    // when the server's view of disabled-ness changes.
    btn.disabled = false;
  };

  const fdFindSubmitButtonsForFormId = (formId) => {
    if (!formId) return [];
    const seen = new Set();
    const buttons = [];
    const form = document.forms[formId];
    if (form) {
      for (const el of Array.from(form.elements)) {
        if (el.type === "submit" && !seen.has(el)) {
          seen.add(el);
          buttons.push(el);
        }
      }
    }
    document.querySelectorAll("button[type='submit'][form]").forEach((btn) => {
      if (btn.getAttribute("form") === formId && !seen.has(btn)) {
        seen.add(btn);
        buttons.push(btn);
      }
    });
    return buttons;
  };

  // A variant change is "in flight" if either a fetch is pending (fdPending)
  // or the option selection has not yet been confirmed by a variant:change
  // event (fdNeedsSync). Both conditions must clear before the button is
  // re-enabled — otherwise a stale form.id (URL-driven variant from before
  // the user clicked) can be submitted on slow networks.
  const fdVariantChangeInFlight = (formId) =>
    fdPending.has(formId) || fdNeedsSync.get(formId) === true;

  // Scroll the variant picker (size guide) into view, respecting the sticky
  // header offset via the theme's `scroll-padding-top` on <html>.
  const fdScrollToVariantPicker = (formId) => {
    const picker = fdGetVariantPickerForForm(formId);
    if (!picker) return;
    const target = picker.closest(".product-info__block-item") || picker;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const fdSyncButtonStateForFormId = (formId) => {
    if (!formId) return;
    const needsSelection = fdRequiresSelection.has(formId) && !fdHasFullSelection(formId);
    const isPending = fdVariantChangeInFlight(formId);

    for (const btn of fdFindSubmitButtonsForFormId(formId)) {
      const isSticky = !!btn.closest("product-sticky-add-to-cart");
      if (needsSelection) {
        fdApplyButtonOverride(btn, fdChooseOptionsLabel, "choose-options");
        // The floating sticky bar is far from the size picker. Instead of an
        // inert disabled button, turn its "choose options" state into a
        // shortcut that scrolls the size guide into view. The submit guard
        // (see the "submit" listener below) still blocks any add-to-cart with
        // an incomplete selection, and the click handler preventDefaults.
        if (isSticky) {
          btn.disabled = false;
          btn.dataset.fdScrollToPicker = formId;
        }
      } else if (isPending) {
        if (isSticky) delete btn.dataset.fdScrollToPicker;
        fdApplyButtonOverride(btn, fdLoadingLabel, "loading");
      } else if (btn.dataset.fdButtonState) {
        fdClearButtonOverride(btn);
      }
    }
  };

  const fdInitFormFromPicker = (picker) => {
    const formId = picker.getAttribute("form-id");
    if (!formId) return;
    // Only ever ADD to fdRequiresSelection. Once a form is flagged as requiring
    // selection on initial page load, the gate is only lifted when the user
    // has clicked through every option position (fdHasFullSelection). A
    // post-rerender variant-picker may legitimately come back without the
    // `requires-selection` attribute (the server-side partial match resolved a
    // variant) — we must NOT use that as a signal to drop the requirement,
    // because the user has not actually clicked every option yet.
    if (picker.hasAttribute("requires-selection")) {
      fdRequiresSelection.add(formId);
    }
    if (!fdClickedPositions.has(formId)) {
      fdClickedPositions.set(formId, new Set());
    }
    fdSyncButtonStateForFormId(formId);
  };

  const fdInitAllVariantPickers = (root) => {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    scope.querySelectorAll("variant-picker[form-id]").forEach(fdInitFormFromPicker);
  };

  fdInitAllVariantPickers(document);

  // When a sticky "choose options" button is activated, scroll to the size
  // picker instead of submitting. Capture phase + preventDefault stops the
  // implicit form submission of this type="submit" button.
  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest?.("[data-fd-scroll-to-picker]");
      if (!trigger) return;
      event.preventDefault();
      fdScrollToVariantPicker(trigger.dataset.fdScrollToPicker);
    },
    true
  );

  const fdClearPending = (formId) => {
    fdPending.delete(formId);
    if (fdFallbackTimers.has(formId)) {
      clearTimeout(fdFallbackTimers.get(formId));
      fdFallbackTimers.delete(formId);
    }
    fdSyncButtonStateForFormId(formId);
  };

  const fdResolvePendingFromSource = (formId, source, extra = {}) => {
    if (!formId || !fdPending.has(formId)) return;

    const now = Date.now();
    const last = fdLastResolveAt.get(formId) ?? 0;
    if (now - last < 50) {
      return;
    }
    fdLastResolveAt.set(formId, now);

    const count = (fdPending.get(formId) ?? 1) - 1;

    if (count <= 0) {
      fdClearPending(formId);
      fdNeedsSync.set(formId, false);
    } else {
      fdPending.set(formId, count);
    }
  };

  // Re-apply button overrides when product:rerender swaps in fresh markup, and
  // re-init variant-picker state when its element is replaced.
  new MutationObserver((mutations) => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const pickers = node.matches?.("variant-picker[form-id]")
          ? [node]
          : [...(node.querySelectorAll?.("variant-picker[form-id]") || [])];
        for (const picker of pickers) {
          fdInitFormFromPicker(picker);
        }

        const btns = node.matches?.("button[type='submit']")
          ? [node]
          : [...(node.querySelectorAll?.("button[type='submit']") || [])];
        for (const btn of btns) {
          const formId = fdGetFormIdForSubmitButton(btn);
          if (formId) {
            fdSyncButtonStateForFormId(formId);
          }
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener(
    "change",
    (event) => {
      if (!event.target.matches("input[data-option-position]")) return;
      const form = fdGetFormForInput(event.target);
      const formId = fdGetFormId(form);
      if (!formId) return;

      const position = event.target.getAttribute("data-option-position");
      if (position) {
        let clicked = fdClickedPositions.get(formId);
        if (!clicked) {
          clicked = new Set();
          fdClickedPositions.set(formId, clicked);
        }
        clicked.add(position);
      }

      const prev = fdPending.get(formId) ?? 0;
      fdPending.set(formId, prev + 1);
      fdNeedsSync.set(formId, true);
      fdSyncButtonStateForFormId(formId);

      if (fdFallbackTimers.has(formId)) {
        clearTimeout(fdFallbackTimers.get(formId));
      }

      const timerId = setTimeout(() => {
        if (!fdPending.has(formId)) return;
        fdClearPending(formId);
      }, 10000);
      fdFallbackTimers.set(formId, timerId);
    },
    true
  );

  document.addEventListener(
    "variant:change",
    (event) => {
      const formId = fdResolveFormId(event.detail?.formId, event.target);
      fdResolvePendingFromSource(formId, "variant:change");
    },
    true
  );

  document.addEventListener(
    "change",
    (event) => {
      if (!event.target.matches('form[action*="/cart/add"] input[name="id"]')) return;
      const formId = fdGetFormId(event.target.form || event.target.closest("form"));
      fdResolvePendingFromSource(formId, "id-input-change");
    },
    true
  );

  // Hard block submissions while a variant change is in flight or the form's
  // variant id is out of sync with the variant picker's selectedVariant.
  // Rationale: when the URL pre-selects a variant (e.g. ?variant=A), the
  // form's hidden id input is populated with A on render. If the user clicks
  // size B on a slow connection and Enters / clicks ATC before the Section
  // Rendering fetch finishes updating form.id.value, the form would submit
  // with the stale id A. The ATC button is already disabled by
  // fdSyncButtonStateForFormId, but this guard catches keyboard submission,
  // third-party scripts, and any other path that could bypass the button.
  document.addEventListener(
    "submit",
    (event) => {
      if (!event.target.matches('form[action*="/cart/add"]')) return;

      const formId = fdGetFormId(event.target);
      if (!formId) return;

      // Block if the user has not yet clicked through every required option.
      if (fdRequiresSelection.has(formId) && !fdHasFullSelection(formId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fdSyncButtonStateForFormId(formId);
        return;
      }

      // Block while a variant change is in flight (fetch pending OR not yet
      // confirmed by variant:change).
      if (fdVariantChangeInFlight(formId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fdSyncButtonStateForFormId(formId);
        return;
      }

      // Defense in depth: if the form's id input doesn't match the variant
      // picker's selectedVariant, the client-side state is desynced from
      // what the user sees. Refuse the submission; the next variant:change
      // (or page reload) will reconcile.
      const formVariantId = event.target.elements?.id?.value;
      const variantPicker = fdGetVariantPickerForForm(formId);
      const pickerVariantId = variantPicker?.selectedVariant?.id
        ? String(variantPicker.selectedVariant.id)
        : null;
      if (
        formVariantId &&
        pickerVariantId &&
        String(formVariantId) !== pickerVariantId
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fdSyncButtonStateForFormId(formId);
      }
    },
    true
  );

  let resizeTimeout;
  let lastBreakpoint = window.innerWidth >= 768 ? "desktop" : "mobile";

  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const currentBreakpoint = window.innerWidth >= 768 ? "desktop" : "mobile";
      
      if (currentBreakpoint !== lastBreakpoint) {
        lastBreakpoint = currentBreakpoint;
        
        document.querySelectorAll("[data-description-collapsible-ready]").forEach((container) => {
          container.removeAttribute("data-description-collapsible-ready");
          
          const toggleButton = container.nextElementSibling;
          if (toggleButton && toggleButton.classList.contains("description-collapsible-toggle")) {
            toggleButton.remove();
          }
        });
        
        initCollapsibleDescriptions(document);
      }
    }, 150);
  });
})();
